'use client';

import Link from 'next/link';
import { MapPin, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardToneClasses, dashboardToneLabel } from './analytics-dashboard-style';
import type { AnalyticsHubLocationTile } from './analytics-dashboard-types';

interface LocationHealthGridProps {
  locations: AnalyticsHubLocationTile[];
}

export function LocationHealthGrid({ locations }: LocationHealthGridProps) {
  const visible = [...locations].sort((a, b) => a.score - b.score);

  return (
    <Card className="rounded-3xl border-[var(--app-border)] bg-[var(--app-surface)] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-primary-600" />
          Здоровье точек
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {visible.length === 0 ? (
          <div className="rounded-2xl bg-[var(--app-surface-soft)] py-10 text-center text-sm text-[var(--app-text-soft)]">
            В выбранном контуре нет активных точек
          </div>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {visible.map((location) => {
              const tone = dashboardToneClasses(location.tone);
              return (
                <Link
                  key={location.id}
                  href={location.href}
                  className={`group rounded-3xl border bg-gradient-to-br p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${tone.border} ${tone.gradient}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-[var(--app-text)]">{location.name}</p>
                      <p className="mt-0.5 truncate text-xs text-[var(--app-text-soft)]">{location.city || 'Точка сети'}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-bold ${tone.soft}`}>
                      {dashboardToneLabel(location.tone)}
                    </span>
                  </div>

                  <div className="mt-4 flex items-end justify-between gap-3">
                    <div>
                      <p className="text-3xl font-black tabular-nums text-[var(--app-text)]">{location.score}</p>
                      <p className="text-[11px] text-[var(--app-text-muted)]">{location.status_label}</p>
                    </div>
                    <ShieldCheck className={`h-7 w-7 ${tone.text}`} />
                  </div>

                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--app-surface)] ">
                    <div className={`h-full rounded-full ${tone.bar}`} style={{ width: `${Math.max(6, location.score)}%` }} />
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
                    <MiniMetric label="Смены" value={`${location.metrics.opened_shifts}/${location.metrics.planned_shifts}`} />
                    <MiniMetric label="Чек" value={`${Math.round(location.metrics.checklists_rate)}%`} />
                    <MiniMetric label="Инц." value={location.metrics.open_incidents} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-2xl bg-[var(--app-surface)] px-2 py-1.5 ">
      <p className="text-sm font-bold tabular-nums text-[var(--app-text)]">{value}</p>
      <p className="text-[10px] text-[var(--app-text-muted)]">{label}</p>
    </div>
  );
}

